import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiChevronRight,
  FiExternalLink,
  FiInfo,
  FiLoader,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiUsers,
  FiX
} from 'react-icons/fi';

import SafeIcon from '../common/SafeIcon';

import CreateBusinessModal from '../components/layout/CreateBusinessModal';
import ClientDetailsDrawer from '../components/clients/ClientDetailsDrawer';

import { useClient } from '../context/ClientContext';

import supabase from '../supabase/supabase';

/*
 * ============================================================
 * HELPERS
 * ============================================================
 */

const formatText = (value) => {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return null;
  }

  return String(value)
    .replace(/_/g, ' ')
    .trim();
};

const formatStatus = (status) => {
  const normalized = formatText(status);

  if (!normalized) return 'Not set';

  return normalized
    .split(' ')
    .map((word) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
};

const getStatusStyle = (status) => {
  const normalized = String(
    status || ''
  ).toLowerCase();

  if (normalized === 'active') {
    return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  }

  if (
    normalized === 'inactive' ||
    normalized === 'archived'
  ) {
    return 'bg-slate-100 text-slate-600 ring-slate-200';
  }

  if (normalized === 'pending') {
    return 'bg-amber-50 text-amber-700 ring-amber-100';
  }

  return 'bg-slate-50 text-slate-500 ring-slate-200';
};

const getInitials = (name) => {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase() || 'CL';
};

const normalizeDetails = (record) => ({
  businessType: record.business_type ?? null,
  industry: record.industry ?? null,
  country: record.country ?? null,
  currency: record.currency ?? null,
  fiscalYear: record.fiscal_year ?? null,
  employeeCount: record.employee_count ?? null,
  monthlyRevenue: record.monthly_revenue ?? null,
  bookkeepingManager: record.bookkeeping_manager ?? null,
  business_email: record.business_email ?? null,
  createdAt: record.created_at ?? null
});

/*
 * ============================================================
 * CLIENT ROW
 * ============================================================
 */

function ClientRow({
  client,
  isSelected,
  onViewDetails,
  onOpenBooks,
  isOpening
}) {
  const details = client.details || {};

  const industry =
    formatText(details.industry) ||
    formatText(client.industry);

  const country =
    formatText(details.country) ||
    formatText(client.country);

  const status = client.status;

  return (
    <tr className="group border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70">

      {/* CLIENT */}
      <td className="px-5 py-4">
        <button
          type="button"
          onClick={() => onViewDetails(client)}
          className="flex min-w-0 items-center gap-3 text-left"
        >
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold ${
            isSelected
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-slate-100 text-slate-600'
          }`}>
            {getInitials(client.name)}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-slate-900 transition-colors group-hover:text-emerald-700">
                {client.name || 'Unnamed client'}
              </span>

              {isSelected && (
                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                  <FiCheck />
                  Selected
                </span>
              )}
            </div>

            {client.legal_name &&
              client.legal_name !== client.name && (
                <p className="mt-1 text-xs text-slate-500">
                  {client.legal_name}
                </p>
              )}
          </div>
        </button>
      </td>

      {/* INDUSTRY */}
      <td className="hidden px-5 py-4 text-[13px] text-slate-600 md:table-cell">
        {industry || (
          <span className="text-slate-400">—</span>
        )}
      </td>

      {/* COUNTRY */}
      <td className="hidden px-5 py-4 text-[13px] text-slate-600 lg:table-cell">
        {country || (
          <span className="text-slate-400">—</span>
        )}
      </td>

      {/* STATUS */}
      <td className="px-5 py-4">
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${getStatusStyle(status)}`}>
          {formatStatus(status)}
        </span>
      </td>

      {/* ACTIONS */}
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onViewDetails(client)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
          >
            Details
            <FiChevronRight className="text-sm" />
          </button>

          <button
            type="button"
            onClick={() => onOpenBooks(client.id)}
            disabled={isOpening}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isOpening ? (
              <FiLoader className="animate-spin" />
            ) : (
              <FiExternalLink />
            )}

            <span className="hidden sm:inline">
              Open Books
            </span>
          </button>
        </div>
      </td>
    </tr>
  );
}

/*
 * ============================================================
 * CLIENTS PAGE
 * ============================================================
 */

export default function Clients() {
  const navigate = useNavigate();

  const {
    user,
    clients,
    activeClient,
    activeFirm,
    switchClient,
    refreshProfile,
    isLoading: workspaceLoading,
    workspaceError
  } = useClient();

  /*
   * ---------------------------------------------------------
   * ACCESS CONTROL
   * ---------------------------------------------------------
   */

  const canCreateClient = Boolean(
    activeFirm?.id &&
    activeFirm?.role === 'owner'
  );

  /*
   * ---------------------------------------------------------
   * PAGE STATE
   * ---------------------------------------------------------
   */

  const [searchQuery, setSearchQuery] = useState('');

  const [statusFilter, setStatusFilter] =
    useState('all');

  const [selectedClientId, setSelectedClientId] =
    useState(null);

  const [isAddClientOpen, setIsAddClientOpen] =
    useState(false);

  const [clientDetails, setClientDetails] =
    useState({});

  const [isLoadingDetails, setIsLoadingDetails] =
    useState(false);

  const [detailsError, setDetailsError] =
    useState(null);

  const [actionError, setActionError] =
    useState(null);

  const [isRefreshing, setIsRefreshing] =
    useState(false);

  const [openingClientId, setOpeningClientId] =
    useState(null);

  /*
   * ---------------------------------------------------------
   * AUTHORIZED CLIENT LIST
   *
   * ClientContext is the primary source.
   *
   * No second settingsService.getAccounts()
   * request or unrelated organization list.
   * ---------------------------------------------------------
   */

  const accessibleClients = useMemo(() => {
    return (clients || [])
      .filter(
        (client) =>
          client?.id &&
          client.isClient === true &&
          client.type === 'client'
      )
      .map((client) => ({
        ...client,
        name:
          client.name ||
          client.legal_name ||
          null
      }));
  }, [clients]);

  /*
   * ---------------------------------------------------------
   * CLIENT IDS
   * ---------------------------------------------------------
   */

  const clientIds = useMemo(
    () =>
      accessibleClients
        .map((client) => client.id)
        .filter(Boolean),
    [accessibleClients]
  );

  const clientIdsKey = clientIds.join(',');

  /*
   * =========================================================
   * FETCH BUSINESS DETAILS
   *
   * Uses authorized client IDs from ClientContext.
   * Never fetches details for unrelated organizations.
   * =========================================================
   */

  useEffect(() => {
    let cancelled = false;

    if (
      !user?.id ||
      workspaceLoading ||
      clientIds.length === 0
    ) {
      setClientDetails({});
      setIsLoadingDetails(false);
      setDetailsError(null);

      return undefined;
    }

    const loadDetails = async () => {
      setIsLoadingDetails(true);
      setDetailsError(null);

      try {
        const { data, error } = await supabase
          .schema('accounting')
          .from('client_profiles')
          .select(`
            organization_id,
            business_type,
            industry,
            country,
            currency,
            fiscal_year,
            employee_count,
            monthly_revenue,
            bookkeeping_manager,
            created_at
          `)
          .in('organization_id', clientIds);

        if (cancelled) return;

        if (error) {
          throw error;
        }

        const mapped = {};

        (data || []).forEach((record) => {
          if (!record.organization_id) return;

          mapped[record.organization_id] =
            normalizeDetails(record);
        });

        setClientDetails(mapped);
      } catch (error) {
        if (cancelled) return;

        console.error(
          'Unable to load client business details:',
          error
        );

        setDetailsError(
          'Some additional business details are unavailable. You can still access your authorized clients.'
        );

        setClientDetails({});
      } finally {
        if (!cancelled) {
          setIsLoadingDetails(false);
        }
      }
    };

    void loadDetails();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    workspaceLoading,
    clientIdsKey
  ]);

  /*
   * ---------------------------------------------------------
   * ENRICH AUTHORIZED CLIENTS
   * ---------------------------------------------------------
   */

  const enrichedClients = useMemo(() => {
    return accessibleClients.map((client) => {
      const details =
        clientDetails[client.id] || {};

      return {
        ...client,

        industry:
          details.industry ??
          client.industry ??
          null,

        country:
          details.country ??
          client.country ??
          null,

        currency:
          details.currency ??
          client.currency ??
          null,

        details,

        ...details
      };
    });
  }, [
    accessibleClients,
    clientDetails
  ]);

  /*
   * ---------------------------------------------------------
   * STATUS OPTIONS FROM REAL CLIENT DATA
   * ---------------------------------------------------------
   */

  const availableStatuses = useMemo(() => {
    return [...new Set(
      enrichedClients
        .map((client) => client.status)
        .filter(
          (status) =>
            status !== null &&
            status !== undefined &&
            String(status).trim()
        )
        .map((status) =>
          String(status).toLowerCase()
        )
    )].sort();
  }, [enrichedClients]);

  /*
   * ---------------------------------------------------------
   * FILTER CLIENTS
   * ---------------------------------------------------------
   */

  const filteredClients = useMemo(() => {
    const query =
      searchQuery.trim().toLowerCase();

    return enrichedClients.filter((client) => {
      const searchableFields = [
        client.name,
        client.legal_name,
        client.industry,
        client.country,
        client.businessType,
        client.bookkeepingManager
      ];

      const matchesSearch =
        !query ||
        searchableFields.some((field) =>
          String(field || '')
            .toLowerCase()
            .includes(query)
        );

      const clientStatus = String(
        client.status || ''
      ).toLowerCase();

      const matchesStatus =
        statusFilter === 'all' ||
        (
          statusFilter === 'not_set'
            ? !clientStatus
            : clientStatus === statusFilter
        );

      return matchesSearch && matchesStatus;
    });
  }, [
    enrichedClients,
    searchQuery,
    statusFilter
  ]);

  /*
   * ---------------------------------------------------------
   * PAGE COUNTS
   * ---------------------------------------------------------
   */

  const totalClients = enrichedClients.length;

  const activeClientsCount =
    enrichedClients.filter(
      (client) =>
        String(client.status || '')
          .toLowerCase() === 'active'
    ).length;

  const hasFilters = Boolean(
    searchQuery.trim() ||
    statusFilter !== 'all'
  );

  /*
   * ---------------------------------------------------------
   * SELECTED CLIENT FOR DRAWER
   *
   * Derived from current authorized state.
   * Never retain stale client details from
   * a previously selected organization.
   * ---------------------------------------------------------
   */

  const selectedClient = useMemo(() => {
    if (!selectedClientId) return null;

    return enrichedClients.find(
      (client) =>
        client.id === selectedClientId
    ) || null;
  }, [
    selectedClientId,
    enrichedClients
  ]);

  /*
   * ---------------------------------------------------------
   * CLOSE STALE DRAWER
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!selectedClientId) return;

    const stillAuthorized =
      accessibleClients.some(
        (client) =>
          client.id === selectedClientId
      );

    if (!stillAuthorized) {
      setSelectedClientId(null);
    }
  }, [
    selectedClientId,
    accessibleClients
  ]);

  /*
   * =========================================================
   * VIEW CLIENT DETAILS
   * =========================================================
   */

  const handleViewDetails = useCallback((client) => {
    if (!client?.id) return;

    setActionError(null);
    setSelectedClientId(client.id);
  }, []);

  /*
   * =========================================================
   * OPEN CLIENT BOOKS
   * =========================================================
   */

  const handleOpenClient = useCallback(
    async (clientId) => {
      if (
        !clientId ||
        openingClientId
      ) {
        return;
      }

      const authorizedClient =
        accessibleClients.find(
          (client) =>
            client.id === clientId
        );

      if (!authorizedClient) {
        setActionError(
          'This client is no longer available in your authorized workspace.'
        );
        return;
      }

      setActionError(null);
      setOpeningClientId(clientId);

      try {
        const result =
          await switchClient(clientId);

        if (!result?.success) {
          throw new Error(
            result?.error ||
            'Unable to open this client.'
          );
        }

        setSelectedClientId(null);

        /*
         * React Router navigation.
         * No window.location.reload().
         *
         * Dashboard will read the newly
         * selected activeClient from Context.
         */
        navigate('/dashboard');
      } catch (error) {
        console.error(
          'Unable to open client books:',
          error
        );

        setActionError(
          error?.message ||
          'Unable to open client books.'
        );
      } finally {
        setOpeningClientId(null);
      }
    },
    [
      accessibleClients,
      switchClient,
      navigate,
      openingClientId
    ]
  );

  /*
   * =========================================================
   * REFRESH CLIENT LIST
   * =========================================================
   */

  const handleRefresh = useCallback(async () => {
    if (
      !user?.id ||
      isRefreshing
    ) {
      return;
    }

    setIsRefreshing(true);
    setActionError(null);

    try {
      const result =
        await refreshProfile();

      if (!result?.success) {
        throw new Error(
          result?.error ||
          'Unable to refresh clients.'
        );
      }
    } catch (error) {
      console.error(
        'Unable to refresh clients:',
        error
      );

      setActionError(
        error?.message ||
        'Unable to refresh clients.'
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [
    user?.id,
    refreshProfile,
    isRefreshing
  ]);

  /*
   * =========================================================
   * CREATE CLIENT
   * =========================================================
   */

  const handleAddClient = () => {
    if (!canCreateClient) {
      setActionError(
        'Only a verified firm owner can create client organizations.'
      );
      return;
    }

    setActionError(null);
    setIsAddClientOpen(true);
  };

  const handleCloseAddClient = () => {
    setIsAddClientOpen(false);

    /*
     * The existing CreateBusinessModal
     * handles creation and context refresh.
     *
     * Refresh after closing as well so this
     * page reflects the latest client list.
     */
    void handleRefresh();
  };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div className="mx-auto w-full max-w-[1450px] space-y-6 pb-8">

      {/* ================================================ */}
      {/* HEADER */}
      {/* ================================================ */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">

        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
            Client Management
          </p>

          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Clients
          </h1>

          <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-500">
            Manage your client companies and access
            their accounting workspaces.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleRefresh()}
            disabled={
              isRefreshing ||
              workspaceLoading ||
              !user?.id
            }
            title="Refresh clients"
            aria-label="Refresh clients"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiRefreshCw
              className={
                isRefreshing
                  ? 'animate-spin'
                  : ''
              }
            />
          </button>

          {canCreateClient && (
            <button
              type="button"
              onClick={handleAddClient}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              <FiPlus className="text-base" />
              Add Client
            </button>
          )}
        </div>
      </div>

      {/* ================================================ */}
      {/* OWNER / FIRM OVERVIEW */}
      {/* ================================================ */}

      {activeFirm && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">

          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <SafeIcon
                icon={FiBriefcase}
                className="text-lg"
              />
            </div>

            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-slate-900">
                {activeFirm.name}
              </p>

              <p className="mt-1 text-xs text-slate-500">
                {formatText(activeFirm.type) || 'Firm workspace'}
                {' · '}
                {formatStatus(activeFirm.role)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-5 sm:gap-8">
            <div>
              <p className="text-xl font-semibold text-slate-900">
                {totalClients}
              </p>

              <p className="mt-1 text-[11px] text-slate-500">
                Total clients
              </p>
            </div>

            <div className="h-9 w-px bg-slate-200" />

            <div>
              <p className="text-xl font-semibold text-slate-900">
                {activeClientsCount}
              </p>

              <p className="mt-1 text-[11px] text-slate-500">
                Active status
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ================================================ */}
      {/* ERRORS / WARNINGS */}
      {/* ================================================ */}

      {actionError && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
        >
          <div className="flex items-start gap-2">
            <FiInfo className="mt-0.5 shrink-0 text-rose-600" />

            <p className="text-xs leading-relaxed text-rose-700">
              {actionError}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setActionError(null)}
            aria-label="Dismiss message"
            className="text-rose-500 hover:text-rose-700"
          >
            <FiX />
          </button>
        </div>
      )}

      {detailsError && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <FiInfo className="mt-0.5 shrink-0 text-amber-600" />

          <p className="text-xs leading-relaxed text-amber-800">
            {detailsError}
          </p>
        </div>
      )}

      {!activeFirm &&
        !workspaceLoading &&
        workspaceError && (
          <div
            role="alert"
            className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700"
          >
            {workspaceError}
          </div>
        )}

      {/* ================================================ */}
      {/* CLIENT DIRECTORY */}
      {/* ================================================ */}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm shadow-slate-900/[0.02]">

        {/* DIRECTORY HEADER */}

        <div className="border-b border-slate-100 px-5 py-5">

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                Client Directory
              </h2>

              <p className="mt-1 text-xs text-slate-500">
                {totalClients} authorized client{
                  totalClients === 1 ? '' : 's'
                }
              </p>
            </div>

            <p className="text-[11px] text-slate-400">
              {filteredClients.length} shown
            </p>
          </div>

          {/* SEARCH AND FILTER */}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">

            <div className="relative min-w-0 flex-1">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="search"
                value={searchQuery}
                onChange={(event) =>
                  setSearchQuery(event.target.value)
                }
                placeholder="Search by name, industry or country..."
                aria-label="Search clients"
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-[13px] text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-50"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value)
              }
              aria-label="Filter clients by status"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 sm:w-44"
            >
              <option value="all">
                All statuses
              </option>

              {availableStatuses.map((status) => (
                <option
                  key={status}
                  value={status}
                >
                  {formatStatus(status)}
                </option>
              ))}

              {enrichedClients.some(
                (client) => !client.status
              ) && (
                <option value="not_set">
                  Not set
                </option>
              )}
            </select>
          </div>
        </div>

        {/* ================================================ */}
        {/* LOADING */}
        {/* ================================================ */}

        {workspaceLoading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 px-6 text-center">
            <FiLoader className="animate-spin text-2xl text-emerald-600" />

            <p className="text-sm text-slate-500">
              Loading your clients...
            </p>
          </div>
        ) : filteredClients.length === 0 ? (
          /*
           * ================================================
           * EMPTY STATE
           * ================================================
           */

          <div className="flex min-h-[300px] flex-col items-center justify-center px-6 py-14 text-center">

            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <FiUsers className="text-2xl" />
            </div>

            <h3 className="mt-5 text-base font-semibold text-slate-900">
              {hasFilters
                ? 'No matching clients'
                : 'No clients yet'}
            </h3>

            <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
              {hasFilters
                ? 'Try another search or change the status filter.'
                : 'Add your first client to start managing their accounting records.'}
            </p>

            {hasFilters ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <FiX />
                Clear Filters
              </button>
            ) : canCreateClient ? (
              <button
                type="button"
                onClick={handleAddClient}
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                <FiPlus />
                Add First Client
              </button>
            ) : null}
          </div>
        ) : (
          /*
           * ================================================
           * CLIENT TABLE
           * ================================================
           */

          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse text-left">

              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Client
                  </th>

                  <th className="hidden px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 md:table-cell">
                    Industry
                  </th>

                  <th className="hidden px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 lg:table-cell">
                    Country
                  </th>

                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Status
                  </th>

                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredClients.map((client) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    isSelected={
                      activeClient?.id === client.id
                    }
                    isOpening={
                      openingClientId === client.id
                    }
                    onViewDetails={handleViewDetails}
                    onOpenBooks={handleOpenClient}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DIRECTORY FOOTER */}

        {!workspaceLoading &&
          filteredClients.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-5 py-3">
              <p className="text-[11px] text-slate-500">
                Showing {filteredClients.length} of{' '}
                {totalClients} clients
              </p>

              {isLoadingDetails && (
                <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <FiLoader className="animate-spin" />
                  Loading details
                </span>
              )}
            </div>
          )}
      </section>

      {/* ================================================ */}
      {/* CREATE CLIENT MODAL */}
      {/* ================================================ */}

      <CreateBusinessModal
        isOpen={
          isAddClientOpen &&
          canCreateClient
        }
        onClose={handleCloseAddClient}
      />

      {/* ================================================ */}
      {/* RIGHT SIDE CLIENT DETAILS DRAWER */}
      {/* ================================================ */}


      <ClientDetailsDrawer
        client={selectedClient}
        clientOrganizationId={selectedClient?.id}
        supabase={supabase}
        canInvite={canCreateClient}
        onClose={() => {
          setSelectedClientId(null);
        }}
        onOpenBooks={() => {
          if (selectedClient?.id) {
            return handleOpenClient(selectedClient.id);
          }
        }}
      />

    </div>
  );
}